import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Chip,
  Stack,
  CircularProgress,
  Alert,
  Grid,
  Button,
  Card,
  CardHeader,
  CardContent,
  Breadcrumbs,
  Link,
  Paper,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Pagination,
  Tabs,
  Tab
} from '@mui/material';
import { 
  School as SchoolIcon,
  NavigateNext,
  LocationOn,
  Language,
  Public,
  People,
  Person,
  CalendarToday,
  Star,
  EmojiEvents,
  Book,
  VideoLibrary,
  Image,
  Description,
  Map,
  OpenInNew,
  Share,
  Bookmark,
  PlayArrow,
  Close,
  PersonAdd,
  Group
} from '@mui/icons-material';
import { facultyService, facultyDataMapper } from '../services/facultyService';
import ShareButtonAdvanced from '../components/common/ShareButtonAdvanced';
import BookmarkButton from '../components/common/BookmarkButton';

const FacultyDetail = () => {
  const { universitySlug, facultySlug } = useParams(); // 使用双参数格式：university-slug/faculty-slug
  const navigate = useNavigate();
  const [faculty, setFaculty] = useState(null);
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [programsPage, setProgramsPage] = useState(1);
  const [programsTotal, setProgramsTotal] = useState(0);
  const [programsStats, setProgramsStats] = useState({ overallTotal: 0, by_degree: { '本科': 0, '硕士': 0, '博士': 0 } });
  const [programsTotalPages, setProgramsTotalPages] = useState(1);
  const [programsLoading, setProgramsLoading] = useState(false);
  const [selectedDegree, setSelectedDegree] = useState('本科');

  const handleViewProgram = (programId) => {
    navigate(`/programs/${programId}`);
  };

  const handleBackToUniversity = () => {
    // 这里需要根据院系信息获取大学ID，暂时返回上一页
    navigate(-1);
  };

  // 加载专业列表（支持分页）
  const loadPrograms = useCallback(async (page = 1) => {
    setProgramsLoading(true);
    try {
      const programsResponse = await facultyService.getFacultyPrograms(
        universitySlug,
        facultySlug,
        { page, size: 15, academic_degree: selectedDegree }
      );
      
      // 调试：打印返回数据
      console.log('专业列表返回数据:', programsResponse);
      
      // apiClient 已经返回了 response.data，所以结构是：{ success, data: { data: [...], total, page, size, totalPages, stats } }
      const programsData = programsResponse?.data || {};
      const programsRaw = programsData.data || [];
      
      console.log('解析后的专业数据:', programsRaw);
      
      const mappedPrograms = programsRaw.map((p) => ({
        id: p.id,
        name: p.name_cn || '',
        nameEn: p.name_en || '',
        level: p.academic_degree || '-',
        mode: p.mode || '课程型',
        duration: p.duration || '-',
        credits: p.credits || '-',
        tuitionFee: p.fees_total || p.fees_tuition,
        currency: p.currency || 'MYR',
        interview: p.is_interview ? '是' : '否',
        portfolio: p.is_portfolio || false,
        crossMajor: p.is_interdisciplinary || false,
        intakes: Array.isArray(p.intakes) ? p.intakes : (typeof p.intakes === 'string' ? p.intakes.split(/[.,，\/\s]+/).filter(Boolean) : []),
        featured: false,
      }));
      
      setPrograms(mappedPrograms);
      // 用 overallTotal 作为“专业总数”（不分学位）
      const stats = programsData.stats || {};
      setProgramsStats({
        overallTotal: stats.overallTotal || 0,
        by_degree: stats.by_degree || { '本科': 0, '硕士': 0, '博士': 0 }
      });
      setProgramsTotal(stats.overallTotal || 0);
      setProgramsTotalPages(programsData.totalPages || 1);
      setProgramsPage(page);
    } catch (err) {
      console.error('加载专业列表失败:', err);
      setPrograms([]);
      setProgramsTotal(0);
      setProgramsTotalPages(1);
    } finally {
      setProgramsLoading(false);
    }
  }, [universitySlug, facultySlug, selectedDegree]);

  // 处理分页变化
  const handlePageChange = useCallback((event, value) => {
    loadPrograms(value);
    // 滚动到专业列表顶部
    setTimeout(() => {
      const programsSection = document.getElementById('programs-section');
      if (programsSection) {
        programsSection.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  }, [loadPrograms]);

  // 切换学位筛选
  const handleDegreeChange = (event, newValue) => {
    if (newValue === null) return;
    setSelectedDegree(newValue);
    // 切换学位后回到第一页
    loadPrograms(1);
  };

  useEffect(() => {
    const loadFacultyData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // 调用真实API获取院系详情
        const response = await facultyService.getFacultyDetail(universitySlug, facultySlug);
        
        // 调试：打印返回数据
        console.log('院系详情返回数据:', response);
        
        // apiClient 已经返回了 response.data，所以结构是：{ success, data: {...} }
        const facultyData = response.data || response;
        
        // 使用数据映射器转换数据
        const mappedFaculty = facultyDataMapper.mapFacultyFromApi(facultyData);
        
        console.log('映射后的院系数据:', mappedFaculty);
        
        setFaculty(mappedFaculty);

        // 加载专业列表（第一页）
        await loadPrograms(1);

      } catch (err) {
        console.error('加载院系详情失败:', err);
        setError(err.responseData?.message || err.message || '加载失败');
      } finally {
        setLoading(false);
      }
    };

    loadFacultyData();
  }, [universitySlug, facultySlug, loadPrograms]);

  if (loading) {
    return (
      <Box sx={{ width: '100%', py: 8, textAlign: 'center' }}>
        <Container maxWidth="xl">
          <CircularProgress size={60} />
          <Typography sx={{ mt: 2 }}>正在加载院系详情...</Typography>
        </Container>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ width: '100%', py: 8, textAlign: 'center' }}>
        <Container maxWidth="xl">
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
          <Typography>无法加载院系信息，请稍后重试。</Typography>
        </Container>
      </Box>
    );
  }

  if (!faculty) {
    return null;
  }

  // 统计卡片组件
  const StatCard = ({ icon: Icon, title, value, color = "primary", subtitle }) => (
    <Card sx={{ height: '100%', transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-2px)' } }}>
      <CardContent sx={{ p: 3, textAlign: 'center' }}>
        <Box
          sx={{
            bgcolor: `${color}.main`,
            color: 'white',
            borderRadius: '50%',
            width: 60,
            height: 60,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 2
          }}
        >
          <Icon sx={{ fontSize: 28 }} />
        </Box>
        <Typography variant="h6" fontWeight={700} color="text.primary" gutterBottom>
          {value || '-'}
        </Typography>
        <Typography variant="body2" color="text.secondary" fontWeight={500}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ width: '100%', py: 4, bgcolor: 'grey.50', minHeight: '100vh' }}>
      <Container maxWidth="xl">
        {/* 面包屑导航 */}
        <Breadcrumbs 
          separator={<NavigateNext fontSize="small" />} 
          sx={{ mb: 3 }}
          aria-label="breadcrumb"
        >
          <Link color="inherit" href="/" underline="hover">
            首页
          </Link>
          <Link color="inherit" href="/universities" underline="hover">
            院校库
          </Link>
          <Link 
            color="inherit" 
            href={`/universities/${universitySlug}`} 
            underline="hover"
            onClick={(e) => {
              e.preventDefault();
              // 使用 universitySlug 跳转到大学详情页
              navigate(`/universities/${universitySlug}`);
            }}
          >
            {faculty.university?.name || '-'}
          </Link>
          <Typography color="text.primary">{faculty.name}</Typography>
        </Breadcrumbs>

        {/* 院系基本信息头部 */}
        <Card sx={{ mb: 4, position: 'relative', overflow: 'visible' }}>
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 120,
              background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
              borderRadius: '12px 12px 0 0',
              zIndex: 0
            }}
          />
          <CardContent sx={{ pt: 8, pb: 4, position: 'relative', zIndex: 1 }}>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={8}>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3, position: 'relative', zIndex: 2 }}>
                  <Chip 
                    label="院系" 
                    color="primary" 
                    variant="filled"
                    sx={{ fontWeight: 600 }}
                  />
                </Stack>
                <Typography variant="h4" fontWeight={700} color="text.primary" gutterBottom sx={{ position: 'relative', zIndex: 2, lineHeight: 1.3 }}>
                  {faculty.name}
                </Typography>
                <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 3, position: 'relative', zIndex: 2, lineHeight: 1.4 }}>
                  {faculty.nameEn}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3, position: 'relative', zIndex: 2 }}>
                  所属大学：{faculty.university?.name || '-'} ({faculty.university?.nameEn || '-'})
                </Typography>
                <Stack direction="row" spacing={3} alignItems="center" sx={{ position: 'relative', zIndex: 2, mt: 2 }}>
                  <Box
                    sx={{
                      position: 'relative',
                      width: 200,
                      height: 80,
                      borderRadius: 2,
                      overflow: 'hidden',
                      bgcolor: 'white',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '3px solid rgba(255,255,255,0.2)',
                      flexShrink: 0
                    }}
                  >
                    {faculty.university?.logo ? (
                      <img
                        src={faculty.university.logo}
                        alt={faculty.university?.name || ''}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                          padding: '8px'
                        }}
                      />
                    ) : (
                      <SchoolIcon sx={{ fontSize: 40, color: 'primary.main' }} />
                    )}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h5" fontWeight={700} sx={{ lineHeight: 1.3, mb: 1 }}>
                      {faculty.nameEn}
                    </Typography>
                    <Typography variant="subtitle1" color="text.secondary" sx={{ lineHeight: 1.4, mb: 0.5 }}>
                      {faculty.name}
                    </Typography>
                    
                  </Box>
                </Stack>
              </Grid>
              <Grid item xs={12} md={4}>
                <Stack spacing={2}>
                  <Stack direction="row" spacing={1}>
                    <BookmarkButton
                      itemId={faculty.id}
                      itemType="faculty"
                      title={faculty.name}
                      description={`查看 ${faculty.name} 的详细信息`}
                      image={faculty.university?.logo}
                      variant="outlined"
                      fullWidth
                      onBookmarkChange={(action, data) => {
                        // 这里可以添加收藏统计逻辑
                      }}
                    />
                    <ShareButtonAdvanced
                      title={faculty.name || '院系详情'}
                      url={window.location.href}
                      description={`查看 ${faculty.name} 的详细信息`}
                      image={faculty.university?.logo}
                      hashtags={['院系', '教育', faculty.university?.name || '大学', faculty.name]}
                      platforms={['wechat', 'weibo', 'qq', 'qzone', 'xiaohongshu', 'douyin', 'copy']}
                      showQRCode={true}
                      showPrint={true}
                      showDownload={false}
                      variant="outlined"
                      fullWidth
                      onShare={(platform, data) => {
                        // 这里可以添加分享统计逻辑
                      }}
                    />
                  </Stack>
                </Stack>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* 主要内容区域 */}
        <Grid container spacing={4}>
          {/* 左侧内容 */}
          <Grid item xs={12} lg={8}>
            {/* 院系介绍 */}
            <Card sx={{ mb: 4 }}>
              <CardContent>
                <Grid container spacing={4}>
                  {/* 院系简介 */}
                  {faculty.content.intro && faculty.content.intro.length > 0 && (
                    <Grid item xs={12}>
                      <Typography variant="h6" fontWeight={600} gutterBottom>
                        院系简介
                      </Typography>
                      {faculty.content.intro.map((intro, index) => (
                        <Typography
                          key={index}
                          variant="body1"
                          sx={{ mb: 2, lineHeight: 1.7 }}
                          dangerouslySetInnerHTML={{ __html: intro.content || '' }}
                        />
                      ))}
                    </Grid>
                  )}

                  {/* 院系优势 */}
                  {faculty.content.advantages && faculty.content.advantages.length > 0 && (
                    <Grid item xs={12}>
                      <Typography variant="h6" fontWeight={600} gutterBottom>
                        院系优势
                      </Typography>
                      <List>
                        {faculty.content.advantages.map((advantage, index) => (
                          <ListItem key={index} sx={{ px: 0 }}>
                            <ListItemIcon>
                              <Star color="primary" />
                            </ListItemIcon>
                            <Typography
                              variant="body2"
                              sx={{ m: 0 }}
                              component="div"
                              dangerouslySetInnerHTML={{ __html: advantage.content || '' }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>

            {/* 专业列表 */}
            <Card id="programs-section">
              <CardHeader
                title={`专业设置 (${programsTotal})`}
                titleTypographyProps={{ variant: 'h6', fontWeight: 600 }}
              />
              <CardContent>
                <Box sx={{ mb: 2 }}>
                  <Tabs
                    value={selectedDegree}
                    onChange={handleDegreeChange}
                    variant="scrollable"
                    scrollButtons="auto"
                    aria-label="degree tabs"
                  >
                    <Tab label="本科" value="本科" />
                    <Tab label="硕士" value="硕士" />
                    <Tab label="博士" value="博士" />
                  </Tabs>
                </Box>
                {programsLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : programs.length > 0 ? (
                  <>
                    <Grid container spacing={3}>
                      {programs.map((program) => (
                        <Grid item xs={12} sm={6} md={4} key={program.id}>
                          <Card 
                            sx={{ 
                              height: '100%', 
                              cursor: 'pointer',
                              transition: 'transform 0.2s',
                              '&:hover': { transform: 'translateY(-2px)' }
                            }}
                            onClick={() => handleViewProgram(program.id)}
                          >
                            
                            <CardContent>
                              <Typography variant="h6" fontWeight={600} gutterBottom>
                                {program.name}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                {program.nameEn}
                              </Typography>
                              <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                                <Chip 
                                  label={program.level} 
                                  size="small" 
                                  color="primary" 
                                />
                                <Chip 
                                  label={program.mode} 
                                  size="small" 
                                  variant="outlined" 
                                />
                              </Stack>
                              <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                                <Typography variant="caption" color="text.secondary">
                                  学制: {program.duration}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  学分: {program.credits}
                                </Typography>
                              </Stack>
                              <Typography variant="caption" color="text.secondary" display="block">
                                学费: {program.currency} {program.tuitionFee?.toLocaleString()}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" display="block">
                                入学时间: {Array.isArray(program.intakes) ? program.intakes.join(', ') : (typeof program.intakes === 'string' ? program.intakes : '-')}
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                    {programsTotalPages > 1 && (
                      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                        <Pagination
                          count={programsTotalPages}
                          page={programsPage}
                          onChange={handlePageChange}
                          color="primary"
                          size="large"
                        />
                      </Box>
                    )}
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                    暂无专业信息
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* 右侧统计信息 */}
          <Grid item xs={12} lg={4}>
            <Stack spacing={3}>
              {/* 院系统计卡片 */}
              <Card>
                <CardHeader
                  title="院系统计"
                  titleTypographyProps={{ variant: 'h6', fontWeight: 600 }}
                />
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <StatCard
                        icon={PersonAdd}
                        title="教师人数"
                        value={faculty.teachers}
                        color="primary"
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <StatCard
                        icon={Group}
                        title="学生人数"
                        value={faculty.students}
                        color="secondary"
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* 专业统计卡片 */}
              <Card>
                <CardHeader
                  title="专业统计"
                  titleTypographyProps={{ variant: 'h6', fontWeight: 600 }}
                />
                <CardContent>
                  <Stack spacing={2}>
                    <Box display="flex" justifyContent="space-between">
                      <Typography>专业总数</Typography>
                      <Typography fontWeight={600}>
                        {programsTotal}
                      </Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <Typography>本科专业</Typography>
                      <Typography fontWeight={600}>
                        {programsStats.by_degree['本科'] || 0}
                      </Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <Typography>硕士专业</Typography>
                      <Typography fontWeight={600}>
                        {programsStats.by_degree['硕士'] || 0}
                      </Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <Typography>博士专业</Typography>
                      <Typography fontWeight={600}>
                        {programsStats.by_degree['博士'] || 0}
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>

              {/* 院长信息卡片 */}
              <Card>
                <CardHeader
                  title="领导信息"
                  titleTypographyProps={{ variant: 'h6', fontWeight: 600 }}
                />
                <CardContent>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      院长
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {faculty.director}
                    </Typography>
                  </Paper>
                </CardContent>
              </Card>

              {/* 返回按钮 */}
              <Card>
                <CardContent>
                  <Button 
                    variant="outlined" 
                    fullWidth
                    onClick={handleBackToUniversity}
                    startIcon={<NavigateNext sx={{ transform: 'rotate(180deg)' }} />}
                  >
                    返回{faculty.university?.name || '大学'}
                  </Button>
                </CardContent>
              </Card>
            </Stack>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default FacultyDetail;
